import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../hooks/useAuth";
import {
  getRoomMeta,
  saveRoomMeta,
  verifyRoomJoin,
  fetchSessionMatch,
  allocateSessionRoomId,
} from "../../../../services/sessionService";
import { prepareArenaInviteShare } from "../../../../services/arenaInvitationService";
import { normalizeMatchRoomCode } from "../../../../utils/matchRoomCode";
import { saveCreatorRoomSecrets } from "../../../../utils/roomCreatorSecrets";
import {
  reportFailedRoomJoin,
  reportInviteJoinCompleted,
  reportMatchJoined,
} from "../../../../features/analytics/bridge/lobbyBridge";
import { reportFirestorePermissionDenied } from "../../../../features/analytics/bridge/errorBridge";
import {
  lobbyCatchErrorMessage,
  lobbyJoinErrorMessage,
  LOBBY_AUTH_REQUIRED_MESSAGE,
} from "../utils/lobbyAuthMessages";
import { markRosterJoinSession } from "../../../../domain/auth/rosterJoinSession";
import { DEFAULT_SPORT, normalizeSportId } from "../../../../domain/sport/sportTypes";
import { setActiveSportId, getActiveSportId } from "../../../../domain/sport/activeSport";
import { listPlayableSportModules } from "../../../../sports";
import { useActiveSport } from "../../../../sports/ActiveSportContext";

const withTimeout = (promise, ms = 20000) =>
  Promise.race([
    promise,
    new Promise((_, rej) =>
      setTimeout(() => rej(new Error("Timeout — check connection")), ms)
    ),
  ]);

/**
 * Create/join room flow for the lobby screen.
 * @param {{
 *   onJoin: (session: object) => void,
 *   initialJoinCode?: string,
 *   initialTab?: "create" | "join",
 *   focusAccessCode?: boolean,
 *   spectatorShareLink?: boolean,
 *   rosterAutoJoin?: boolean,
 * }} options
 */
export function useLobby({
  onJoin,
  initialJoinCode = "",
  initialTab = "create",
  focusAccessCode = false,
  spectatorShareLink = false,
  rosterAutoJoin = false,
}) {
  const { uid, ensureSessionReady } = useAuth();
  const { sportId: activeSportId } = useActiveSport();
  const passInputRef = useRef(null);
  const autoSpectatorJoinDone = useRef(false);
  const autoRosterJoinDone = useRef(false);
  const [tab, setTab] = useState(initialTab === "join" ? "join" : "create");
  const [rName, setRName] = useState("");
  const [refC, setRefC] = useState("");
  const [viewC, setViewC] = useState("");
  const [joinC, setJoinC] = useState("");
  const [passC, setPassC] = useState("");
  const [creatorPlaysInSession, setCreatorPlaysInSession] = useState(true);
  /** Always inherit Global Active Sport — no local sport picker. */
  const sportId = normalizeSportId(activeSportId || getActiveSportId() || DEFAULT_SPORT);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [createInvite, setCreateInvite] = useState(null);

  useEffect(() => {
    if (!initialJoinCode) return;
    setJoinC(initialJoinCode);
    setTab("join");
    setErr("");
  }, [initialJoinCode]);

  useEffect(() => {
    if (!focusAccessCode || tab !== "join") return;
    const id = requestAnimationFrame(() => {
      passInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [focusAccessCode, tab, initialJoinCode]);

  const switchTab = (next) => {
    setTab(next);
    setErr("");
    if (next !== "create") setCreateInvite(null);
  };

  const resolveSessionUid = async () => {
    if (uid) return uid;
    try {
      return (await ensureSessionReady()) || null;
    } catch {
      return null;
    }
  };

  const create = async () => {
    const displayName = rName.trim();
    if (!displayName) {
      setErr("Enter an arena name");
      return;
    }
    if (!refC.trim()) {
      setErr("Set a referee password");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const sessionUid = await resolveSessionUid();
      if (!sessionUid) {
        setErr(LOBBY_AUTH_REQUIRED_MESSAGE);
        setLoading(false);
        return;
      }
      const selectedSportId = normalizeSportId(sportId || DEFAULT_SPORT);
      const playable = listPlayableSportModules().some((m) => m.id === selectedSportId);
      if (!playable) {
        setErr("Choose a playable sport");
        setLoading(false);
        return;
      }
      const sessionId = await withTimeout(allocateSessionRoomId());
      await withTimeout(
        saveRoomMeta(sessionId, {
          name: displayName,
          refCode: refC.trim(),
          viewCode: viewC.trim() || "",
          createdAt: Date.now(),
          ownerUid: sessionUid,
          creatorPlaysInSession,
          sportId: selectedSportId,
        })
      );
      setActiveSportId(selectedSportId);
      saveCreatorRoomSecrets(sessionUid, sessionId, {
        refCode: refC.trim(),
        viewCode: viewC.trim(),
      });
      // Gate share UI until server can see the arena + link invitation exists.
      const ready = await withTimeout(
        prepareArenaInviteShare({
          arenaId: sessionId,
          arenaName: displayName,
        }),
        25000
      );
      if (!ready?.ok) {
        setErr(
          ready?.message ||
            "Arena created locally but invite is not ready yet. Open the arena and share from Players."
        );
        setCreateInvite({
          joinPayload: {
            room: sessionId,
            role: "referee",
            roomDisplayName: displayName,
            registerAsCreator: true,
            creatorPlaysInSession,
            sportId: selectedSportId,
          },
          spectatorCode: viewC.trim(),
          refCode: refC.trim(),
          inviteAbsUrl: "",
          inviteReady: false,
          inviteError: ready?.message || "Invite link not ready",
        });
        setLoading(false);
        return;
      }
      setCreateInvite({
        joinPayload: {
          room: sessionId,
          role: "referee",
          roomDisplayName: displayName,
          registerAsCreator: true,
          creatorPlaysInSession,
          sportId: selectedSportId,
        },
        spectatorCode: viewC.trim(),
        refCode: refC.trim(),
        inviteAbsUrl: ready.inviteAbsUrl,
        invitationId: ready.invitation?.invitationId || null,
        inviteReady: true,
      });
    } catch (e) {
      const sessionUid = await resolveSessionUid();
      setErr(lobbyCatchErrorMessage(e, { hasUid: Boolean(sessionUid) }));
    }
    setLoading(false);
  };

  const attemptJoin = async (roomCode, accessCode, joinOpts = {}) => {
    const meta = await withTimeout(getRoomMeta(roomCode));
    if (!meta) {
      return { ok: false, stage: "meta", reason: "not_found" };
    }
    const joinResult = await withTimeout(
      verifyRoomJoin(roomCode, accessCode, joinOpts)
    );
    if (!joinResult.ok) {
      return { ok: false, stage: "verify", reason: joinResult.reason, meta };
    }
    return { ok: true, meta, role: joinResult.role };
  };

  const completeJoinFromResult = async (r, result) => {
    const sessionUid = await resolveSessionUid();
    let role = result.role;
    let registerAsCreator = false;
    let creatorPlaysInSession = true;
    try {
      const match = await fetchSessionMatch(r);
      if (sessionUid && match?.createdBy === sessionUid) {
        role = "referee";
        registerAsCreator = true;
      }
    } catch {
      /* non-fatal */
    }
    void fetchSessionMatch(r);
    // Roster before navigate so host/joiner see the seat immediately.
    if (sessionUid && !registerAsCreator) {
      try {
        const { ensureSessionJoinerOnRoster } = await import(
          "../../../../services/session/ensureSessionJoinerOnRoster"
        );
        await ensureSessionJoinerOnRoster({
          roomCode: r,
          uid: sessionUid,
          role: "player",
          retries: 3,
        });
      } catch {
        /* shell entry retries */
      }
    }
    onJoin({
      room: r,
      role,
      roomDisplayName: result.meta.name || r,
      registerAsCreator,
      creatorPlaysInSession,
      rosterInviteLanding: rosterAutoJoin,
    });
    if (rosterAutoJoin) {
      markRosterJoinSession(true);
    }
    reportMatchJoined(role, {
      hasViewCode: Boolean(result.meta.viewCode),
      registerAsCreator,
      joinSource: rosterAutoJoin ? "link" : spectatorShareLink ? "link" : "code",
    });
    if (rosterAutoJoin) {
      reportInviteJoinCompleted(role, { registerAsCreator, rosterInvite: true });
    } else if (spectatorShareLink) {
      reportInviteJoinCompleted(role, { spectatorInvite: true });
    }
  };

  const spectatorJoinOpts = (accessCode) => {
    const hasCode = String(accessCode || "").trim().length > 0;
    if (hasCode) return {};
    if (spectatorShareLink) return { spectatorOnly: true };
    return {};
  };

  useEffect(() => {
    if (
      !spectatorShareLink ||
      !initialJoinCode ||
      !uid ||
      autoSpectatorJoinDone.current
    ) {
      return undefined;
    }
    let cancelled = false;
    autoSpectatorJoinDone.current = true;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        let sessionUid = await resolveSessionUid();
        if (!sessionUid) {
          sessionUid = (await ensureSessionReady()) || null;
        }
        if (cancelled || !sessionUid) {
          if (!cancelled) setLoading(false);
          return;
        }
        const r = normalizeMatchRoomCode(initialJoinCode);
        if (!r) {
          setLoading(false);
          return;
        }
        let result = await attemptJoin(r, "", { spectatorOnly: true });
        if (cancelled) return;
        if (result.ok) {
          completeJoinFromResult(r, result);
          setLoading(false);
          return;
        }
        if (result.stage === "verify" && result.reason === "wrong_code") {
          setErr(
            lobbyJoinErrorMessage("wrong_code", {
              spectatorLink: spectatorShareLink,
            })
          );
        }
      } catch (e) {
        if (!cancelled) {
          const sessionUid = await resolveSessionUid();
          setErr(lobbyCatchErrorMessage(e, { hasUid: Boolean(sessionUid) }));
        }
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [spectatorShareLink, initialJoinCode, uid]);

  useEffect(() => {
    if (!rosterAutoJoin || !initialJoinCode || !uid || autoRosterJoinDone.current) {
      return undefined;
    }
    let cancelled = false;
    autoRosterJoinDone.current = true;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        let sessionUid = await resolveSessionUid();
        if (!sessionUid) {
          sessionUid = (await ensureSessionReady()) || null;
        }
        if (cancelled || !sessionUid) {
          if (!cancelled) setLoading(false);
          return;
        }
        const r = normalizeMatchRoomCode(initialJoinCode);
        if (!r) {
          setLoading(false);
          return;
        }
        let result = await attemptJoin(r, "", { spectatorOnly: true });
        if (cancelled) return;
        if (result.ok) {
          completeJoinFromResult(r, result);
          setLoading(false);
          return;
        }
        if (result.stage === "verify" && result.reason === "wrong_code") {
          setTab("join");
          setJoinC(r);
          setErr(
            lobbyJoinErrorMessage("wrong_code", {
              spectatorLink: true,
            })
          );
        }
      } catch (e) {
        if (!cancelled) {
          const sessionUid = await resolveSessionUid();
          setErr(lobbyCatchErrorMessage(e, { hasUid: Boolean(sessionUid) }));
        }
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [rosterAutoJoin, initialJoinCode, uid]);

  const join = async () => {
    const r = normalizeMatchRoomCode(joinC);
    if (!r) {
      setErr("Enter an arena code");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      let sessionUid = await resolveSessionUid();
      if (!sessionUid) {
        setErr(LOBBY_AUTH_REQUIRED_MESSAGE);
        reportFailedRoomJoin("auth_required");
        setLoading(false);
        return;
      }

      const code = passC.trim();
      const joinOpts = spectatorJoinOpts(code);

      let result = await attemptJoin(r, code, joinOpts);
      if (!result.ok && result.stage === "verify" && result.reason === "auth_required") {
        sessionUid = (await ensureSessionReady()) || null;
        if (sessionUid) {
          result = await attemptJoin(r, code, joinOpts);
        }
      }

      if (!result.ok) {
        if (result.stage === "meta" && result.reason === "not_found") {
          setErr(lobbyJoinErrorMessage("not_found"));
          reportFailedRoomJoin("not_found");
        } else if (result.stage === "verify") {
          const reason = result.reason || "wrong_code";
          if (reason === "rules") {
            reportFirestorePermissionDenied("room_join");
          }
          reportFailedRoomJoin(
            reason === "auth_required"
              ? "auth_required"
              : reason === "rules"
                ? "rules"
                : "wrong_code"
          );
          setErr(
            lobbyJoinErrorMessage(reason, {
              retriedAuth: reason === "auth_required" && Boolean(sessionUid),
              spectatorLink: spectatorShareLink,
            })
          );
        }
        setLoading(false);
        return;
      }

      completeJoinFromResult(r, result);
    } catch (e) {
      const sessionUid = await resolveSessionUid();
      setErr(lobbyCatchErrorMessage(e, { hasUid: Boolean(sessionUid) }));
    }
    setLoading(false);
  };

  const submit = tab === "create" ? create : join;

  const enterCreatedRoom = () => {
    if (!createInvite?.joinPayload) return;
    onJoin(createInvite.joinPayload);
    setCreateInvite(null);
  };

  const dismissCreateInvite = () => {
    setCreateInvite(null);
  };

  return {
    tab,
    switchTab,
    rName,
    setRName,
    refC,
    setRefC,
    viewC,
    setViewC,
    joinC,
    setJoinC,
    passC,
    setPassC,
    err,
    loading,
    submit,
    passInputRef,
    creatorPlaysInSession,
    setCreatorPlaysInSession,
    sportId,
    spectatorShareLink,
    createInvite,
    enterCreatedRoom,
    dismissCreateInvite,
  };
}
